import { Container, Graphics, Sprite, Text } from "pixi.js";
import type { Texture } from "pixi.js";
import { FACTIONS } from "@frontier/content/factions";
import type { PublicEventView } from "@frontier/content/events";
import { ShipSilhouetteGlow } from "./ship-silhouette-glow";
import { relayStatus } from "../relay-ui";

interface RelayView {
  container: Container;
  boundary: Graphics;
  sprite: Sprite;
  glow: ShipSilhouetteGlow;
  label: Text;
}

/** The ring represents the authoritative capture area, independently of selection. */
export class RelayViews {
  readonly container = new Container({ label: "frontier-relays" });
  private readonly views = new Map<string, RelayView>();

  constructor(private readonly texture: Texture) {}

  update(events: readonly PublicEventView[]): void {
    const relays = events.filter((event) => event.type === "relay" && event.capture);
    const ids = new Set(relays.map((event) => event.id));
    for (const [id, view] of this.views) {
      if (ids.has(id)) continue;
      view.glow.destroy();
      view.container.destroy({ children: true });
      this.views.delete(id);
    }
    for (const event of relays) {
      const capture = event.capture;
      if (!capture) continue;
      let view = this.views.get(event.id);
      if (!view) {
        const container = new Container({ label: `relay:${event.id}` });
        const boundary = new Graphics({ label: "capture-area" });
        const sprite = new Sprite({ texture: this.texture, anchor: 0.5 });
        sprite.label = "relay-station";
        sprite.width = 110;
        sprite.scale.y = sprite.scale.x;
        const label = new Text({ text: "", style: { fontFamily: "Exo 2", fontSize: 12, fill: 0xe4c58f, align: "center" } });
        label.anchor.set(0.5);
        container.addChild(boundary, sprite, label);
        view = { container, boundary, sprite, glow: new ShipSilhouetteGlow(container, sprite), label };
        this.views.set(event.id, view);
        this.container.addChild(container);
      }
      const color = capture.contested ? 0xee957f : capture.factionId
        ? Number.parseInt(FACTIONS[capture.factionId].color.slice(1), 16) : 0xe4c58f;
      const active = event.status === "active";
      view.container.position.set(event.x, event.y);
      view.sprite.alpha = event.status === "available" ? 0.45 : 1;
      view.glow.sync(color);
      view.boundary.clear().circle(0, 0, capture.radius)
        .fill({ color, alpha: active ? 0.025 : 0.008 })
        .stroke({ color, width: active ? 2 : 1, alpha: active ? 0.5 : 0.2 });
      if (event.progress > 0)
        view.boundary.arc(0, 0, capture.radius, -Math.PI / 2,
          -Math.PI / 2 + Math.PI * 2 * Math.min(1, event.progress / event.goal))
          .stroke({ color, width: 4, alpha: 0.8 });
      view.label.text = `${event.title}\n${relayStatus(event)}`;
      view.label.style.fill = color;
      view.label.position.set(0, -capture.radius - 22);
    }
  }

  destroy(): void {
    for (const view of this.views.values()) view.glow.destroy();
    this.views.clear();
    this.container.destroy({ children: true });
  }
}
